import L from "leaflet";
import { EditBevoorradingsZonesStatus } from '../../../constants/ComponentConstants';
import {
    DrawRouteOptions,
    DrawZoneOptions,
    RouteEditStyle,
    ZoneStyle,
    DRAW_START_ZOOM
} from './MapConstants';

export const MapEditModes = Object.freeze({
    VIEW: 'view',
    DRAW_ROUTE: 'draw-route',
    EDIT_ROUTE: 'edit-route',
    EDIT_ZONES: 'edit-zones'
});

export default class MapEditController {
    constructor({ map, renderer, onRouteDrawnChanged, onZonesStatusChanged }) {
        this.map = map;
        this.renderer = renderer;
        this.mode = MapEditModes.VIEW;
        this.zonesStatus = EditBevoorradingsZonesStatus.EDITING;
        this.drawnRouteLayer = null;
        this.onRouteDrawnChanged = onRouteDrawnChanged;
        this.onZonesStatusChanged = onZonesStatusChanged;
        map.on('pm:create', this.onPmCreate, this);
    }

    destroy() {
        this.map.off('pm:create', this.onPmCreate, this);
    }

    onPmCreate(e) {
        if (e.shape === 'Line' && this.mode === MapEditModes.DRAW_ROUTE) {
            e.layer.setStyle(RouteEditStyle);
            e.layer.pm.enable({ allowSelfIntersection: true });
            this.drawnRouteLayer = e.layer;
            this.notifyRouteDrawn(true);
        }

        if (e.shape === 'Polygon' && this.mode === MapEditModes.EDIT_ZONES) {
            e.layer.setStyle(ZoneStyle);
            e.layer.pm.enable({ allowSelfIntersection: true });
            this.renderer.adoptZone(e.layer);
        }
    }

    startDrawRoute(panToCenter) {
        this.mode = MapEditModes.DRAW_ROUTE;
        this.drawnRouteLayer = null;
        this.notifyRouteDrawn(false);
        this.map.pm.setGlobalOptions({ snappable: false });
        this.map.pm.enableDraw('Line', DrawRouteOptions);

        if (panToCenter)
            this.map.setView(new L.LatLng(panToCenter[0], panToCenter[1]), DRAW_START_ZOOM);
    }

    confirmDrawRoute() {
        const layer = this.drawnRouteLayer;
        this.disableAllDraw();

        let feature = null;
        if (layer) {
            feature = layer.toGeoJSON();
            this.map.removeLayer(layer);
        }

        this.drawnRouteLayer = null;
        this.mode = MapEditModes.VIEW;
        this.notifyRouteDrawn(false);
        return feature;
    }

    cancelDrawRoute() {
        this.disableAllDraw();
        if (this.drawnRouteLayer)
            this.map.removeLayer(this.drawnRouteLayer);
        this.drawnRouteLayer = null;
        this.mode = MapEditModes.VIEW;
        this.notifyRouteDrawn(false);
    }

    startEditRoute() {
        this.mode = MapEditModes.EDIT_ROUTE;
        this.map.pm.setGlobalOptions({ snappable: false });
        this.renderer.getRouteLayers().forEach(layer => layer.pm.enable({ allowSelfIntersection: true }));
    }

    startEditZones() {
        this.mode = MapEditModes.EDIT_ZONES;
        if (this.renderer.hasZones())
            this.zonesEdit();
        else
            this.zonesAdd();
    }

    zonesAdd() {
        this.setZonesStatus(EditBevoorradingsZonesStatus.ADDING);
        this.map.pm.disableGlobalRemovalMode();
        this.renderer.eachZone(layer => layer.pm.disable());
        this.map.pm.setGlobalOptions({ snappable: true });
        this.map.pm.enableDraw('Polygon', DrawZoneOptions);
    }

    zonesEdit() {
        this.setZonesStatus(EditBevoorradingsZonesStatus.EDITING);
        this.disableAllDraw();
        this.map.pm.disableGlobalRemovalMode();
        this.map.pm.setGlobalOptions({ snappable: false });
        this.renderer.eachZone(layer => layer.pm.enable({ allowSelfIntersection: true }));
    }

    zonesRemove() {
        this.setZonesStatus(EditBevoorradingsZonesStatus.REMOVING);
        this.disableAllDraw();
        this.renderer.eachZone(layer => layer.pm.disable());
        // alleen zones zijn verwijderbaar: alle andere layers hebben allowRemoval=false
        this.map.pm.enableGlobalRemovalMode();
    }

    confirmEdit() {
        this.stopAllEditing();
        const model = this.renderer.modelFromLayers();
        this.mode = MapEditModes.VIEW;
        return model;
    }

    cancelEdit() {
        this.stopAllEditing();
        this.mode = MapEditModes.VIEW;
    }

    stopAllEditing() {
        this.disableAllDraw();
        this.map.pm.disableGlobalRemovalMode();
        this.map.pm.setGlobalOptions({ snappable: false });
        this.renderer.getRouteLayers().forEach(layer => this.disableEdit(layer));
        this.renderer.eachZone(layer => this.disableEdit(layer));
        this.setZonesStatus(EditBevoorradingsZonesStatus.EDITING);
        this.drawnRouteLayer = null;
        this.notifyRouteDrawn(false);
    }

    disableEdit(layer) {
        if (layer.pm && layer.pm.enabled())
            layer.pm.disable();
    }

    disableAllDraw() {
        this.map.pm.disableDraw('Line');
        this.map.pm.disableDraw('Polygon');
    }

    setZonesStatus(status) {
        this.zonesStatus = status;
        if (this.onZonesStatusChanged)
            this.onZonesStatusChanged(status);
    }

    notifyRouteDrawn(drawn) {
        if (this.onRouteDrawnChanged)
            this.onRouteDrawnChanged(drawn);
    }
}
